import { useSelector } from 'react-redux';

import { CalendarIcon } from '@radix-ui/react-icons';
import { Flex, Label, Text, Tooltip } from '@raystack/apsara';
import { createFormattedAreaEl } from '@studio/map';
import clsx from 'clsx';
import * as _ from 'lodash';

import { Header } from '@src/components/Custom/Header';
import ImageDivThumb from '@src/containers/Explore/subComponents/Table/ImageDiv/ImageThumbnail';
import { For, Show } from '@src/hoc';

import { ReactComponent as IdIcon } from './IdIcon.svg';

import styles from './style.module.css';

const ScenesInfo = ({ title, onClose, selectedSceneInfo, selector }) => {
  const activeAoi = useSelector(state => state.projects.activeAoi);
  const activeAoiCheck = activeAoi && !_.isEmpty(activeAoi);

  const imageList = selector.getImageList?.(selectedSceneInfo) ?? [];

  const schema = [
    {
      label: 'ID',
      getValue: () => (
        <Show when={!!selector.getImageId}>
          <Tooltip
            message={selector.getImageId?.(selectedSceneInfo)}
            side='top'
          >
            <div className={styles.imageId}>
              <span className={styles.ellipses}>
                {selector.getImageId?.(selectedSceneInfo)}
              </span>
            </div>
          </Tooltip>
        </Show>
      ),
      show: !!selector.getImageId
    },
    {
      label: 'Date',
      getValue: () => selector.getDate(selectedSceneInfo),
      show: true
    },
    {
      label: 'Satellite',
      getValue: () => selector.getSatellite(selectedSceneInfo),
      show: true
    },
    {
      label: 'AOI Coverage',
      getValue: () =>
        activeAoiCheck ? (
          `${Math.round(
            (selector.getArea?.(selectedSceneInfo) / activeAoi.area_in_sq_m) *
              100
          )}%`
        ) : (
          <>--</>
        ),
      show: !!selector.getArea
    },
    {
      label: 'Cloud Coverage',
      getValue: () => selector.getCloudCoverage?.(selectedSceneInfo),
      show: !!selector.getCloudCoverage
    },
    {
      label: 'Bands',
      getValue: () => selector.getBandsLen(selectedSceneInfo),
      show: true
    },
    {
      label: 'Area',
      getValue: () =>
        selector.getArea?.(selectedSceneInfo) === -1
          ? 'NA'
          : createFormattedAreaEl(selector.getArea?.(selectedSceneInfo) / 1e6),
      show: !!selector.getArea
    },
    {
      label: 'Center',
      getValue: () => selector.getCenter?.(selectedSceneInfo),
      show: !!selector.getCenter
    },
    {
      label: 'Max GSD',
      getValue: () => selector.getGsd?.(selectedSceneInfo),
      show: !!selector.getGsd
    },
    {
      label: 'Azimuth angle',
      getValue: () => selector.getAzimuthAngle?.(selectedSceneInfo),
      show: !!selector.getAzimuthAngle
    },
    {
      label: 'Sun elevation',
      getValue: () => selector.getSunElevation?.(selectedSceneInfo),
      show: !!selector.getSunElevation
    },
    {
      label: 'Collection',
      getValue: () => selector.getCollection?.(selectedSceneInfo),
      show: !!selector.getCollection
    },
    {
      label: 'Off-Nadir angle',
      getValue: () => selector.getLookAngle?.(selectedSceneInfo),
      show: !!selector.getLookAngle
    },
    {
      label: 'Tiles',
      getValue: () => imageList.length,
      show: !!selector.getImageList
    }
  ];

  return (
    <div className={styles.Infocontainer}>
      <Header onClose={onClose}>
        <Header.Text>{title}</Header.Text>
      </Header>

      <div className={styles.InfoDiv}>
        <Flex className={styles.aoiInfoContentDiv} direction='column'>
          <For each={schema}>
            {d => (
              <Show when={d.show}>
                <Flex>
                  <Text className={styles.label}>{d.label}</Text>
                  <Text weight={500}>{d.getValue()}</Text>
                </Flex>
              </Show>
            )}
          </For>
        </Flex>

        <For each={imageList}>
          {tile => (
            <div className={styles.SceneInfoTilesDiv}>
              <ImageDivThumb
                src={tile.thumbnail_url}
                css={{
                  width: '48px',
                  height: '48px',
                  borderRadius: 'var(--rs-radius-2)'
                }}
              />
              <div className={styles.TilesContent}>
                <div className={styles.tileContent}>
                  <CalendarIcon className={styles.iconStyle} />
                  <Label size='small'>{tile.date}</Label>
                </div>

                <div className={styles.tileContent}>
                  <IdIcon
                    className={styles.iconStyle}
                    style={{
                      stroke: 'var(--rs-color-foreground-base-primary)'
                    }}
                  />
                  <Tooltip
                    message={tile?.id}
                    side='top'
                    contentStyle={{ maxWidth: 'min-content' }}
                  >
                    <Label
                      size='small'
                      className={clsx(styles.text, styles.ellipses)}
                    >
                      {tile?.id}
                    </Label>
                  </Tooltip>
                </div>
              </div>
            </div>
          )}
        </For>
      </div>
    </div>
  );
};

export default ScenesInfo;
